/**
 * Sentry helpers — user identity + manual capture (CORE-308).
 *
 * `@sentry/react-native` is the single Sentry SDK used across web + iOS +
 * Android (see App.tsx init), so these wrappers work on every platform. Every
 * call is wrapped in try/catch because Sentry.init may not have run yet
 * (bootstrap-time crash) or may be disabled in __DEV__ — telemetry must never
 * take down the surrounding UI.
 *
 * Why a wrapper instead of calling Sentry.* directly at each site:
 *  - one place to normalise the user shape (CORE-308 wants id + role; no email since CORE-1405);
 *  - one place to coerce non-Error throwables into Error so stack traces and
 *    grouping survive;
 *  - call sites stay a one-liner: `captureError(e, { tags: { flow: 'auth' } })`.
 */
import * as Sentry from '@sentry/react-native';
import { describeApiError } from './apiErrorIdentity';

export type SentryUserInfo = {
  id: string;
  /** Raw role list from the auth payload (e.g. ['master'] / ['admin']). */
  roles?: string[] | null;
};

/** Pick the most specific role for quick triage filtering in Sentry. */
function primaryRole(roles?: string[] | null): string {
  if (!roles || roles.length === 0) return 'user';
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('master')) return 'master';
  return roles[0];
}

/**
 * Attach the signed-in user to every subsequent Sentry event so the team can
 * answer «кто столкнулся с проблемой» without spelunking. id + role are the
 * triage keys. Email is deliberately NOT attached (CORE-1405): it is personal
 * data at a third party, and the id finds the person in the admin panel anyway.
 * Pass null/undefined to clear (logout).
 */
export function setSentryUser(user: SentryUserInfo | null | undefined): void {
  try {
    if (!user || !user.id) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({
      id: user.id,
      // Custom fields ride along on the Sentry user object and show up in the
      // event "User" section + are filterable in the issue search.
      role: primaryRole(user.roles),
      roles: user.roles ?? undefined,
    } as Sentry.User);
  } catch {
    // Sentry not initialised / disabled — identity is best-effort.
  }
}

/** Clear the Sentry user identity (logout). */
export function clearSentryUser(): void {
  try {
    Sentry.setUser(null);
  } catch {
    // no-op
  }
}

export type CaptureContext = {
  /** Indexed, searchable key/values — keep low-cardinality (flow, status). */
  tags?: Record<string, string | number | boolean | undefined>;
  /** Free-form structured detail attached to the event. */
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
};

/**
 * CORE-1099 — grouping key for a manually captured error.
 *
 * Sentry groups these events by culprit, and the culprit of every one of them
 * is `captureError` itself, so unrelated failures land in one issue: in
 * REACT-NATIVE-31 two real 500s sat inside fourteen expected 404s and nobody
 * saw them. Appending the flow and the HTTP status to the default fingerprint
 * splits that issue along the two axes triage actually uses — «какой поток» и
 * «какой отказ» — so the next genuine 5xx opens its own issue instead of
 * disappearing into someone else's.
 *
 * Returns undefined when there is no flow tag: without it there is nothing to
 * split by, and overriding the fingerprint would only make grouping worse.
 */
export function captureFingerprint(
  error: unknown,
  tags?: CaptureContext['tags'],
): string[] | undefined {
  const flow = tags?.flow;
  if (typeof flow !== 'string' || flow === '') return undefined;
  const status = (error as { status?: unknown } | null | undefined)?.status ?? tags?.status;
  if (typeof status === 'number' || (typeof status === 'string' && status !== '')) {
    return ['{{ default }}', flow, String(status)];
  }
  return ['{{ default }}', flow];
}

/**
 * Manually report a handled error to Sentry with structured context
 * (CORE-308 item 5 — critical user flows: uploads, video processing, practice
 * / live creation, auth). Coerces non-Error throwables into an Error so the
 * event keeps a stack and groups sensibly. Swallowed `AbortError`s and
 * user-cancellations should be filtered by the caller before calling this.
 */
export function captureError(error: unknown, context?: CaptureContext): void {
  try {
    // CORE-1999 — an HTTP error from api.ts: short title without traceId and
    // one group per «method + path template + status», whatever the flow.
    const api = describeApiError(error);
    if (api) {
      const apiErr = new Error(api.title);
      apiErr.name = 'ApiError';
      Sentry.captureException(apiErr, {
        tags: context?.tags as Record<string, string> | undefined,
        // The body (with traceId) stays searchable as detail, not as the title.
        extra: { ...context?.extra, apiError: { ...(error as object), method: api.method, path: api.path } },
        level: context?.level,
        // Without the request (an error object built by hand) the flow is the
        // best stand-in for «which endpoint».
        fingerprint: api.path
          ? ['api-error', api.method ?? '-', api.path, String(api.status)]
          : ['api-error', String(context?.tags?.flow ?? '-'), String(api.status)],
      });
      return;
    }
    const err =
      error instanceof Error
        ? error
        : new Error(
            typeof error === 'string'
              ? error
              : (() => {
                  try {
                    return JSON.stringify(error);
                  } catch {
                    return String(error);
                  }
                })(),
          );
    Sentry.captureException(err, {
      tags: context?.tags as Record<string, string> | undefined,
      extra: context?.extra,
      level: context?.level,
      fingerprint: captureFingerprint(error, context?.tags),
    });
  } catch {
    // Telemetry must never crash the caller.
  }
}

/**
 * CORE-1999 — record an expected outcome (e.g. «room already deleted») as a
 * breadcrumb: visible in the trail of the next real event, never an event of
 * its own.
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  try {
    Sentry.addBreadcrumb({ category, message, level: 'info', data });
  } catch {
    // Telemetry must never crash the caller.
  }
}
